import { OpenGovAppConfig } from '@opengov/app-config';
import { BODY_CONTENT, CONTENT, FONTS_WITH_TINYMCE_GOOGLE_FONTS } from '@opengov/stories-shared/constants';
import { IStoryContent } from '@opengov/stories-shared/types';
import { DATA_MERGE_TAG_ID } from '@opengov/stories-shared/utils';

import { Editor } from '@tinymce/tinymce-react';
import type { IProps } from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { Editor as TinyMCE } from 'tinymce/tinymce';
import { IEditorMessages, IModalMessages } from '../../../../../../../types';

import { AiExplainThisModal } from '../../../../../../../modals/components/AiExplainThisModal';
import { AiPromptModal } from '../../../../../../../modals/components/AiPromptModal';
import { assertUnreachable } from '../../../../../../../types/typeUtils';
import {
  MAX_NUMBER_OF_MERGE_TAGS,
  removeListTagsForSingleMergeTag,
  updateMergeTagHtmlId,
  useMergeTagsContext
} from '../../../../../../common/context/MergeTagsContext';
import { ISubtypeColors } from '../../../../../../utils/editor';
import { EditorCommand, useCurrentEditorContext } from '../../../../CurrentEditorContext';
import { EditorValueSource } from '../../tinyMCEStoryEditorUtils';
import { TEXT_SELECTION, addTextContextMenuToEditor } from './contextMenus/textContextMenu';
import { addSlashCommandToEditor } from './slashCommands';
import {
  ITinyMCEDialog,
  getContentOptions,
  getFilePickerCallback,
  getForegroundColorOptions,
  getImagesUploadHandler
} from './tinyMCEEditorUtils';

export const TINYMCE_TOOLBAR_CONTAINER_ID = 'tinymce-toolbar-container';
const operateUrl = OpenGovAppConfig.OPERATE_URL;

function getRawEditorOptions(canUseAiToGenerateContent: boolean): IProps['init'] {
  return {
    menubar: false,
    automatic_uploads: true,
    image_title: true,
    toolbar_mode: 'floating',
    contextmenu: canUseAiToGenerateContent ? `table spellchecker ${TEXT_SELECTION}` : 'table spellchecker',
    table_resize_bars: true,
    spellchecker_language: 'en',
    autocorrect_capitalize: true,
    block_formats: 'Paragraph=p; Header 1=h1; Header 2=h2; Header 3=h3',
    link_title: false,
    link_context_toolbar: true,
    default_link_target: '_blank',
    paste_data_images: false,
    powerpaste_allow_local_images: false,
    powerpaste_word_import: 'prompt',
    powerpaste_html_import: 'prompt',
    powerpaste_googledocs_imports: 'prompt',
    a11y_advanced_options: true,
    image_advtab: true,
    file_picker_types: 'image',
    editimage_toolbar: '',
    plugins: ['link', 'lists', 'advlist', 'charmap', 'table', 'advtable', 'powerpaste', 'image', 'editimage'],
    advlist_number_styles: 'default',
    formats: {
      alignleft: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img,audio,video', classes: 'align-left' },
      aligncenter: {
        selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img,audio,video',
        classes: 'align-center'
      },
      alignright: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img,audio,video', classes: 'align-right' },
      alignjustify: {
        selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img,audio,video',
        classes: 'align-justify'
      }
    },
    font_family_formats: FONTS_WITH_TINYMCE_GOOGLE_FONTS.map(
      ({ name, fallback }) => `${name}="${name}", ${fallback}`
    ).join('; '),
    fixed_toolbar_container: `#${TINYMCE_TOOLBAR_CONTAINER_ID}`,
    noneditable_class: 'noneditable',
    browser_spellcheck: true
  };
}

export interface ITinyMCEEditorProps extends Partial<IProps> {
  window: Window;
  entitySubdomain: string;
  fontColors: ISubtypeColors;
  messages: IEditorMessages;
  storyContent?: IStoryContent;
  contentSeed?: number;
  onInit?(editor: TinyMCE): void;
  onChange(newValue: string, uuid: string): void;
  editorValueSource: EditorValueSource;
  entityId: number;
  setIsLoading: (isLoading: boolean) => void;
  canUseAiToGenerateContent: boolean;
  canUseStoriesSummarizerGA: boolean;
  modalMessages: IModalMessages;
  isAiEnabledByAdmin: boolean;
}
export const TinyMCEEditor = (props: ITinyMCEEditorProps) => {
  const {
    storyContent,
    onInit,
    onChange,
    inline = true,
    entitySubdomain,
    fontColors,
    editorValueSource,
    setIsLoading,
    entityId,
    canUseAiToGenerateContent,
    isAiEnabledByAdmin,
    contentSeed,
    ...restProps
  } = props;
  const activeDialogRef = useRef<ITinyMCEDialog | null>(null);
  const filePickerCallback = useMemo(() => {
    return getFilePickerCallback(entitySubdomain, activeDialogRef);
  }, [entitySubdomain, activeDialogRef]);
  const imagesUploadHandler = useMemo(() => {
    return getImagesUploadHandler(entitySubdomain);
  }, [entitySubdomain]);

  const [value, setValue] = useState<string>(props.value);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const { setCurrentEditor, execCommand } = useCurrentEditorContext();
  const { storyMergeTagList } = useMergeTagsContext();
  const isSectionContent = useMemo(
    () => storyContent.type === BODY_CONTENT || storyContent.type === CONTENT,
    [storyContent.content]
  );
  const editorOptions = useMemo(() => {
    return {
      ...getRawEditorOptions(canUseAiToGenerateContent),
      ...getContentOptions(isSectionContent),
      ...getForegroundColorOptions(fontColors),
      file_picker_callback: filePickerCallback,
      images_upload_handler: imagesUploadHandler,
      setup: (editor: TinyMCE) => {
        editor.on('OpenWindow', (editorEvent) => {
          activeDialogRef.current = editorEvent.dialog;
        });
      }
    };
  }, [filePickerCallback, imagesUploadHandler, isSectionContent, fontColors]);
  const storyMergeTagPasteEnabled = storyMergeTagList.length < MAX_NUMBER_OF_MERGE_TAGS;

  useEffect(() => {
    if (storyContent.shouldClear) {
      setValue('');
    }
  }, [storyContent.shouldClear]);

  useEffect(() => {
    if (storyContent.shouldUpdate) {
      setValue(props.value);
    }
  }, [storyContent.shouldUpdate, props.value]);

  const appliedContentSeed = useRef<number>(contentSeed);
  useEffect(() => {
    if (contentSeed === appliedContentSeed.current) return;
    appliedContentSeed.current = contentSeed;
    setValue(props.value);
  }, [contentSeed, props.value]);

  useEffect(() => {
    if (editorValueSource.variant === 'ai-editing') {
      switch (editorValueSource.status) {
        case 'generating':
          // Do nothing
          break;

        case 'complete':
          setValue(props.value);
          editorValueSource.resetValueSourceToUser();
          break;

        case 'error':
          // Reset value source to user without setting editor value
          editorValueSource.resetValueSourceToUser();
          break;

        default:
          assertUnreachable(editorValueSource.status);
      }
    }
  }, [props.value, editorValueSource]);

  return (
    <>
      <Editor
        {...restProps}
        tinymceScriptSrc={`${operateUrl}/stories/assets/tinymce/tinymce.min.js`}
        inline={inline}
        value={value}
        init={editorOptions}
        onInit={(event, editor) => {
          // Note: slash commands are only added if AI is enabled by admin
          // because slash commands and AI-related features went live together
          // Ideally, we'd enable slash commands for everyone and only
          // toggle the AI-related features.
          if (canUseAiToGenerateContent && isAiEnabledByAdmin) {
            addSlashCommandToEditor({
              editor,
              messages: props.messages.slashCommands,
              openAiPrompt: () => setIsPromptModalOpen(true)
            });
            addTextContextMenuToEditor({
              editor,
              setIsGenerating: setIsLoading,
              setExplanation: (explanation) => setAiExplanation(explanation),
              entityId,
              messages: props.messages
            });
          }

          if (onInit) onInit(editor);
        }}
        onEditorChange={(newValue, editor) => {
          setValue(newValue);
          onChange(newValue, editor.getElement()?.parentElement?.getAttribute('data-uuid'));
        }}
        onFocusIn={(event, editor) => setCurrentEditor(editor)}
        onPaste={(event) => {
          const content = event.clipboardData.getData('text/html');
          if (content.indexOf(DATA_MERGE_TAG_ID) > -1) {
            event.preventDefault();
            if (storyMergeTagPasteEnabled) {
              execCommand({
                command: EditorCommand.MceInsertContent,
                content: removeListTagsForSingleMergeTag(updateMergeTagHtmlId(content))
              });
            }
          }
        }}
      />
      {isPromptModalOpen
        ? createPortal(
          <AiPromptModal
            messages={props.messages.aiPrompt}
            value={value}
            entityId={entityId}
            closeModal={() => setIsPromptModalOpen(false)}
            setValue={setValue}
          />,
          document.getElementById('app')
        )
        : null}

      {aiExplanation ? (
        <AiExplainThisModal
          explanation={aiExplanation}
          closeModal={() => setAiExplanation(null)}
          messages={props.messages.explainThis}
        />
      ) : null}
    </>
  );
};
